import { useState } from 'react';
import {
  Button,
  H2,
  H3,
  Input,
  Paragraph,
  Spinner,
  Switch,
  Text,
  TextArea,
  XStack,
  YStack,
} from 'tamagui';
import { Screen } from '@mrrmlab/ui';
import type { StoreArticle, StoreDepartment } from '@mrrmlab/shared-types';
import {
  useCreateStoreArticle,
  useCreateStoreDepartment,
  useDeleteStoreArticle,
  useDeleteStoreDepartment,
  useReorderStoreDepartments,
  useStoreArticles,
  useStoreDepartments,
  useUpdateStoreArticle,
  useUpdateStoreDepartment,
} from '../catalog-hooks';

export interface StoreCatalogScreenProps {
  storeId: string;
  storeName?: string;
  onBack?: () => void;
}

type ActiveTab = 'articles' | 'departments';

export function StoreCatalogScreen({ storeId, storeName, onBack }: StoreCatalogScreenProps) {
  const [tab, setTab] = useState<ActiveTab>('articles');

  return (
    <Screen scrollable>
      <XStack alignItems="center" gap="$3">
        {onBack ? (
          <Button size="$2" chromeless onPress={onBack}>
            ← Zurück
          </Button>
        ) : null}
        <H2 flex={1}>{storeName ? `${storeName} – Katalog` : 'Artikelkatalog'}</H2>
      </XStack>

      <XStack gap="$2">
        <Button
          size="$3"
          flex={1}
          chromeless={tab !== 'articles'}
          borderWidth={tab === 'articles' ? 1 : 0}
          onPress={() => setTab('articles')}
        >
          Artikel
        </Button>
        <Button
          size="$3"
          flex={1}
          chromeless={tab !== 'departments'}
          borderWidth={tab === 'departments' ? 1 : 0}
          onPress={() => setTab('departments')}
        >
          Abteilungen
        </Button>
      </XStack>

      {tab === 'articles' ? (
        <ArticlesTab storeId={storeId} />
      ) : (
        <DepartmentsTab storeId={storeId} />
      )}
    </Screen>
  );
}

// ---------------------------------------------------------------------------
// Articles tab
// ---------------------------------------------------------------------------

function ArticlesTab({ storeId }: { storeId: string }) {
  const articlesQuery = useStoreArticles(storeId);
  const departmentsQuery = useStoreDepartments(storeId);
  const createArticle = useCreateStoreArticle(storeId);
  const [name, setName] = useState('');
  const [notes, setNotes] = useState('');
  const [departmentId, setDepartmentId] = useState<string | null>(null);
  const [pickingDept, setPickingDept] = useState(false);

  const departments = departmentsQuery.data ?? [];
  const articles = articlesQuery.data ?? [];

  const canSubmit = name.trim().length > 0;

  const submit = () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    createArticle.mutate(
      { name: trimmed, departmentId: departmentId ?? undefined, notes: notes.trim() || undefined },
      {
        onSuccess: () => {
          setName('');
          setNotes('');
          setDepartmentId(null);
        },
      },
    );
  };

  // group articles by department
  const grouped = groupArticlesByDepartment(articles, departments);

  return (
    <YStack gap="$3">
      <YStack
        gap="$2"
        padding="$3"
        borderRadius="$3"
        borderWidth={1}
        borderColor="$borderColor"
        backgroundColor="$color2"
      >
        <H3 fontSize="$4">Neuer Artikel</H3>
        <Input
          placeholder="Artikelname"
          value={name}
          onChangeText={setName}
          onSubmitEditing={submit}
        />
        <TextArea
          placeholder="Notiz (optional)"
          value={notes}
          onChangeText={setNotes}
          numberOfLines={2}
        />
        {pickingDept ? (
          <YStack gap="$2">
            <Text fontSize="$3" opacity={0.7}>Abteilung wählen:</Text>
            <XStack gap="$2" flexWrap="wrap">
              <Button size="$2" onPress={() => { setDepartmentId(null); setPickingDept(false); }}>
                Keine
              </Button>
              {departments.map((d) => (
                <Button
                  key={d.id}
                  size="$2"
                  chromeless={departmentId !== d.id}
                  borderWidth={departmentId === d.id ? 1 : 0}
                  onPress={() => { setDepartmentId(d.id); setPickingDept(false); }}
                >
                  {d.name}
                </Button>
              ))}
            </XStack>
          </YStack>
        ) : (
          <XStack alignItems="center" gap="$2">
            <Text fontSize="$3" opacity={0.7}>
              Abteilung:{' '}
              {departmentId
                ? (departments.find((d) => d.id === departmentId)?.name ?? '–')
                : 'Keine'}
            </Text>
            <Button size="$2" chromeless onPress={() => setPickingDept(true)}>
              Ändern
            </Button>
          </XStack>
        )}
        <Button disabled={createArticle.isPending || !canSubmit} onPress={submit}>
          Artikel hinzufügen
        </Button>
      </YStack>

      {articlesQuery.isPending ? (
        <Spinner />
      ) : articlesQuery.isError ? (
        <Paragraph color="$red10">Fehler: {articlesQuery.error.message}</Paragraph>
      ) : articles.length === 0 ? (
        <Paragraph opacity={0.7}>Noch keine Artikel. Oben eintragen.</Paragraph>
      ) : (
        <YStack gap="$4">
          {grouped.map(({ department, items }) => (
            <YStack key={department?.id ?? '__none__'} gap="$2">
              <Text fontSize="$3" fontWeight="600" opacity={0.5} textTransform="uppercase">
                {department?.name ?? 'Sonstiges'}
              </Text>
              {items.map((article) => (
                <ArticleCard
                  key={article.id}
                  article={article}
                  storeId={storeId}
                  departments={departments}
                />
              ))}
            </YStack>
          ))}
        </YStack>
      )}
    </YStack>
  );
}

function ArticleCard({
  article,
  storeId,
  departments,
}: {
  article: StoreArticle;
  storeId: string;
  departments: StoreDepartment[];
}) {
  const updateArticle = useUpdateStoreArticle(storeId);
  const deleteArticle = useDeleteStoreArticle(storeId);
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState('');
  const [notes, setNotes] = useState('');
  const [departmentId, setDepartmentId] = useState<string | null>(null);
  const [pickingDept, setPickingDept] = useState(false);

  const startEdit = () => {
    setName(article.name);
    setNotes(article.notes ?? '');
    setDepartmentId(article.departmentId);
    setEditing(true);
  };

  const save = () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    updateArticle.mutate(
      {
        id: article.id,
        dto: {
          name: trimmed,
          notes: notes.trim() || null,
          departmentId: departmentId,
        },
      },
      { onSuccess: () => setEditing(false) },
    );
  };

  const busy = updateArticle.isPending || deleteArticle.isPending;

  return (
    <YStack
      gap="$2"
      padding="$3"
      borderRadius="$3"
      borderWidth={1}
      borderColor="$borderColor"
      backgroundColor="$background"
      opacity={article.active ? 1 : 0.5}
    >
      {editing ? (
        <>
          <Input value={name} onChangeText={setName} placeholder="Artikelname" />
          <TextArea value={notes} onChangeText={setNotes} placeholder="Notiz (optional)" numberOfLines={2} />
          {pickingDept ? (
            <XStack gap="$2" flexWrap="wrap">
              <Button size="$2" onPress={() => { setDepartmentId(null); setPickingDept(false); }}>Keine</Button>
              {departments.map((d) => (
                <Button
                  key={d.id}
                  size="$2"
                  chromeless={departmentId !== d.id}
                  borderWidth={departmentId === d.id ? 1 : 0}
                  onPress={() => { setDepartmentId(d.id); setPickingDept(false); }}
                >
                  {d.name}
                </Button>
              ))}
            </XStack>
          ) : (
            <XStack alignItems="center" gap="$2">
              <Text fontSize="$3" opacity={0.7}>
                Abteilung:{' '}
                {departmentId ? (departments.find((d) => d.id === departmentId)?.name ?? '–') : 'Keine'}
              </Text>
              <Button size="$2" chromeless onPress={() => setPickingDept(true)}>Ändern</Button>
            </XStack>
          )}
          <XStack gap="$2" justifyContent="flex-end">
            <Button size="$2" chromeless onPress={() => setEditing(false)}>Abbrechen</Button>
            <Button size="$2" disabled={busy || name.trim().length === 0} onPress={save}>Speichern</Button>
          </XStack>
        </>
      ) : (
        <XStack alignItems="flex-start" gap="$2">
          <YStack flex={1} gap="$1">
            <Text fontSize="$4" fontWeight="600">
              {article.name}
            </Text>
            {article.notes ? (
              <Text fontSize="$3" opacity={0.6}>{article.notes}</Text>
            ) : null}
          </YStack>
          <XStack gap="$1" alignItems="center">
            <Switch
              size="$2"
              checked={article.active}
              onCheckedChange={(checked) =>
                updateArticle.mutate({ id: article.id, dto: { active: checked } })
              }
              disabled={busy}
            >
              <Switch.Thumb animation="quick" />
            </Switch>
            <Button size="$2" chromeless onPress={startEdit}>✎</Button>
            <Button
              size="$2"
              chromeless
              disabled={busy}
              onPress={() => deleteArticle.mutate(article.id)}
            >
              ✕
            </Button>
          </XStack>
        </XStack>
      )}
    </YStack>
  );
}

// ---------------------------------------------------------------------------
// Departments tab
// ---------------------------------------------------------------------------

function DepartmentsTab({ storeId }: { storeId: string }) {
  const departmentsQuery = useStoreDepartments(storeId);
  const createDepartment = useCreateStoreDepartment(storeId);
  const reorder = useReorderStoreDepartments(storeId);
  const [name, setName] = useState('');

  const departments = departmentsQuery.data ?? [];

  const submit = () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    createDepartment.mutate({ name: trimmed }, { onSuccess: () => setName('') });
  };

  const moveUp = (index: number) => {
    if (index === 0) return;
    const ids = departments.map((d) => d.id);
    const next = [...ids];
    [next[index - 1]!, next[index]!] = [next[index]!, next[index - 1]!];
    reorder.mutate({ orderedIds: next });
  };

  const moveDown = (index: number) => {
    if (index === departments.length - 1) return;
    const ids = departments.map((d) => d.id);
    const next = [...ids];
    [next[index]!, next[index + 1]!] = [next[index + 1]!, next[index]!];
    reorder.mutate({ orderedIds: next });
  };

  return (
    <YStack gap="$3">
      <XStack gap="$2" alignItems="center">
        <Input
          flex={1}
          placeholder="Neue Abteilung"
          value={name}
          onChangeText={setName}
          onSubmitEditing={submit}
        />
        <Button disabled={createDepartment.isPending || name.trim().length === 0} onPress={submit}>
          +
        </Button>
      </XStack>

      {departmentsQuery.isPending ? (
        <Spinner />
      ) : departmentsQuery.isError ? (
        <Paragraph color="$red10">Fehler: {departmentsQuery.error.message}</Paragraph>
      ) : departments.length === 0 ? (
        <Paragraph opacity={0.7}>Noch keine Abteilungen. Oben eintragen.</Paragraph>
      ) : (
        <YStack gap="$2">
          {departments.map((dept, index) => (
            <DepartmentCard
              key={dept.id}
              department={dept}
              storeId={storeId}
              canMoveUp={index > 0}
              canMoveDown={index < departments.length - 1}
              onMoveUp={() => moveUp(index)}
              onMoveDown={() => moveDown(index)}
              busy={reorder.isPending}
            />
          ))}
        </YStack>
      )}
    </YStack>
  );
}

function DepartmentCard({
  department,
  storeId,
  canMoveUp,
  canMoveDown,
  onMoveUp,
  onMoveDown,
  busy,
}: {
  department: StoreDepartment;
  storeId: string;
  canMoveUp: boolean;
  canMoveDown: boolean;
  onMoveUp: () => void;
  onMoveDown: () => void;
  busy: boolean;
}) {
  const updateDepartment = useUpdateStoreDepartment(storeId);
  const deleteDepartment = useDeleteStoreDepartment(storeId);
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState('');

  const startEdit = () => {
    setName(department.name);
    setEditing(true);
  };

  const save = () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    updateDepartment.mutate(
      { id: department.id, dto: { name: trimmed } },
      { onSuccess: () => setEditing(false) },
    );
  };

  const isBusy = busy || updateDepartment.isPending || deleteDepartment.isPending;

  return (
    <XStack
      alignItems="center"
      gap="$2"
      padding="$3"
      borderRadius="$3"
      borderWidth={1}
      borderColor="$borderColor"
      backgroundColor="$background"
    >
      <YStack gap="$1">
        <Button size="$2" chromeless disabled={!canMoveUp || isBusy} onPress={onMoveUp}>▲</Button>
        <Button size="$2" chromeless disabled={!canMoveDown || isBusy} onPress={onMoveDown}>▼</Button>
      </YStack>

      {editing ? (
        <>
          <Input
            flex={1}
            value={name}
            onChangeText={setName}
            onSubmitEditing={save}
            autoFocus
          />
          <Button size="$2" chromeless disabled={isBusy} onPress={save}>✓</Button>
          <Button size="$2" chromeless onPress={() => setEditing(false)}>✕</Button>
        </>
      ) : (
        <>
          <Text flex={1} fontSize="$4" fontWeight="600">{department.name}</Text>
          <Button size="$2" chromeless onPress={startEdit}>✎</Button>
          <Button
            size="$2"
            chromeless
            disabled={isBusy}
            onPress={() => deleteDepartment.mutate(department.id)}
          >
            ✕
          </Button>
        </>
      )}
    </XStack>
  );
}

// ---------------------------------------------------------------------------
// Helper
// ---------------------------------------------------------------------------

function groupArticlesByDepartment(
  articles: StoreArticle[],
  departments: StoreDepartment[],
): Array<{ department: StoreDepartment | null; items: StoreArticle[] }> {
  const groups = new Map<string | null, StoreArticle[]>();

  for (const article of articles) {
    const key = article.departmentId;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)!.push(article);
  }

  const result: Array<{ department: StoreDepartment | null; items: StoreArticle[] }> = [];

  // departments in order
  for (const dept of departments) {
    const items = groups.get(dept.id) ?? [];
    if (items.length > 0) result.push({ department: dept, items });
  }

  // articles without department
  const unassigned = groups.get(null) ?? [];
  if (unassigned.length > 0) result.push({ department: null, items: unassigned });

  return result;
}
